import React, { useMemo } from 'react';
import Table from '../../components/Table/Table';
import { FilterBar } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import type { Job, Application } from './types';
import { getStatusBadge } from './types';

interface ApplicationsTabProps {
  applications: Application[];
  jobs: Job[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  applicationStatusFilter: string;
  onApplicationStatusFilterChange: (value: string) => void;
  dateFromFilter: string;
  onDateFromFilterChange: (value: string) => void;
  dateToFilter: string;
  onDateToFilterChange: (value: string) => void;
  onClearFilters: () => void;
  onEditApplication: (application: Application) => void;
  onViewApplication: (application: Application) => void;
  onRateApplication: (application: Application) => void;
}

const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  applications, jobs, searchTerm, onSearchChange,
  applicationStatusFilter, onApplicationStatusFilterChange,
  dateFromFilter, onDateFromFilterChange,
  dateToFilter, onDateToFilterChange,
  onClearFilters, onEditApplication, onViewApplication, onRateApplication,
}) => {
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      const job = jobs.find(j => j.id === app.jobId);
      const matchesSearch = !searchTerm || (
        app.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job?.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesApplicationStatus = applicationStatusFilter === 'all' || app.status === applicationStatusFilter;
      const appliedDate = new Date(app.appliedDate);
      const matchesDateFrom = !dateFromFilter || appliedDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || appliedDate <= new Date(dateToFilter);
      return matchesSearch && matchesApplicationStatus && matchesDateFrom && matchesDateTo;
    });
  }, [applications, jobs, searchTerm, applicationStatusFilter, dateFromFilter, dateToFilter]);

  const handleExport = () => {
    const exportData = filteredApplications.map(app => {
      const job = jobs.find(j => j.id === app.jobId);
      return {
        'Application ID': app.id,
        'Job Title': job?.title || 'Unknown',
        'Candidate Name': app.candidateName,
        Email: app.email,
        Phone: app.phone,
        'Applied Date': app.appliedDate,
        Status: app.status,
        'Total Rating': app.totalRating || 0,
        'Assessment Points': app.rating.length,
        'Evaluated By': app.evaluatedBy || 'Not evaluated',
        'Evaluated At': app.evaluatedAt ? new Date(app.evaluatedAt).toLocaleDateString() : 'Not evaluated',
        Notes: app.notes || '',
        'Resume File': app.resume
      };
    });
    exportToExcel(exportData, `job_applications_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredApplications.map(app => {
      const job = jobs.find(j => j.id === app.jobId);
      return {
        'Candidate Name': app.candidateName,
        'Job Title': job?.title || 'N/A',
        Email: app.email,
        Phone: app.phone,
        'Applied Date': app.appliedDate,
        Status: app.status,
        Rating: app.totalRating,
        Notes: app.notes || '',
        'Resume File': app.resume
      };
    });
    printAsExcel(exportData, 'Job Applications');
  };

  const filters: FilterConfig[] = [
    {
      key: 'applicationStatus', label: 'Status', type: 'select',
      value: applicationStatusFilter, onChange: onApplicationStatusFilterChange,
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'Pending', label: 'Pending' },
        { value: 'Reviewed', label: 'Reviewed' },
        { value: 'Interview', label: 'Interview' },
        { value: 'Hired', label: 'Hired' },
        { value: 'Rejected', label: 'Rejected' },
      ],
    },
    { key: 'dateFrom', label: 'From Date', type: 'date', value: dateFromFilter, onChange: onDateFromFilterChange },
    { key: 'dateTo', label: 'To Date', type: 'date', value: dateToFilter, onChange: onDateToFilterChange },
  ];

  const columns = [
    { key: 'id', header: 'Application ID', label: 'Application ID' },
    {
      key: 'jobId', header: 'Job Title', label: 'Job Title',
      render: (value: string) => {
        const job = jobs.find(j => j.id === value);
        return job?.title || 'Unknown';
      },
    },
    { key: 'candidateName', header: 'Candidate Name', label: 'Candidate Name' },
    { key: 'email', header: 'Email', label: 'Email' },
    { key: 'phone', header: 'Phone', label: 'Phone' },
    { key: 'appliedDate', header: 'Applied Date', label: 'Applied Date' },
    {
      key: 'resume', header: 'CV', label: 'CV',
      render: (value: string) => (
        <button
          onClick={() => value ? window.open(value, '_blank') : alert('No CV uploaded')}
          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          📄 View CV
        </button>
      ),
    },
    {
      key: 'totalRating', header: 'Rating', label: 'Rating', sortable: true,
      render: (value: number, item: Application) => (
        <div className="flex items-center">
          {item.rating.length > 0 ? (
            <div className="flex items-center">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-xs font-medium text-primary-800">{value || 0}</span>
              </div>
              <span className="ml-2 text-xs text-gray-500">/10</span>
              <span className="ml-2 text-xs text-gray-400">
                ({item.rating.length} point{item.rating.length !== 1 ? 's' : ''})
              </span>
            </div>
          ) : (
            <span className="text-sm text-gray-500">Not rated</span>
          )}
        </div>
      ),
    },
    {
      key: 'status', header: 'Status', label: 'Status',
      render: (value: string) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(value)}`}>{value}</span>
      ),
    },
    {
      key: 'actions', header: 'Actions', label: 'Actions',
      render: (_value: any, application: Application) => (
        <div className="flex items-center space-x-2">
          <button onClick={() => onEditApplication(application)} className="text-blue-600 hover:text-blue-800" title="Edit application">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button onClick={() => onViewApplication(application)} className="text-gray-600 hover:text-gray-800" title="View application details">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button onClick={() => onRateApplication(application)} className="text-purple-600 hover:text-purple-800" title="Rate candidate">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 713.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 713.138-3.138z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search applications..."
        filters={filters}
        onClearFilters={onClearFilters}
        totalCount={applications.length}
        filteredCount={filteredApplications.length}
        entityName="Applications"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        data={filteredApplications}
        columns={columns}
        title="Applications"
        showAddButton={false}
        showEditButton={false}
        showDeleteButton={false}
        searchable={false}
      />
    </>
  );
};

export default ApplicationsTab;
